import { css } from '@emotion/css';

import { Trans, useTranslation } from '@lyearn/i18n';
import { DefaultModal, TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import VirtualizedList from '@/components/VirtualizedList';
import usePagination from '@/hooks/usePagination';
import { CONTENT_TYPE_LOWER_CASE_i18n_CONFIG } from '@/modules/content/config';
import RESOURCE_TYPE_LABEL_CONFIG from '@/modules/content/config/label';
import renderImage from '@/modules/content/utils/renderImage';
import { useGetParentContentsQuery } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/queries/generated/getParentContents';
import { ContentEntity, ResourceTypeEnum } from '@/types/schema';

const setHeight = (height: number) => css`
  height: ${height}px;
`;

interface ImpactedItemsDialogProps {
  onClose: () => void;
  id: string;
  contentType: ResourceTypeEnum;
}

function ImpactedItemsDialog({ onClose, id, contentType }: ImpactedItemsDialogProps) {
  const { t } = useTranslation();

  const { fetchMore, result } = usePagination(
    useGetParentContentsQuery,
    {
      filters: { id: id },
    },
    { edgeKey: 'parentContents' },
  );

  const data = result.data?.parentContents?.contents;

  const height = Math.min(data?.length! * 72, 680);

  const itemContent = (index: number) => {
    const content = data?.[index] as ContentEntity;
    return (
      <Stack className="border-b border-divider" direction="col">
        <Stack alignItems="center" className="p-12" gapX={12}>
          {renderImage({
            image: content.cover?.url,
            width: 48,
            height: 48,
            contentName: content.name,
            className: 'w-48 h-48 object-cover',
          })}
          <Stack className="min-w-0" direction="col" gapY={2}>
            <TypographyWithTooltip numberOfLines={1} variant="body-short-01">
              {content.name}
            </TypographyWithTooltip>
            <Typography color="text-text-secondary" variant="body-short-02">
              {t(RESOURCE_TYPE_LABEL_CONFIG[content.type as ResourceTypeEnum].title)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };
  return (
    <DefaultModal
      contentClassName={cn(setHeight(height))}
      header={{
        title: t('Admin:ItemsImpacted'),
        description: (
          <Trans
            i18nKey="Admin:Contents.ChangeDescription"
            values={{ contentType: t(CONTENT_TYPE_LOWER_CASE_i18n_CONFIG[contentType]) }}
          />
        ),
      }}
      primaryAction={{
        appearance: 'primary',
        label: t('Common:GotIt'),
        onClick: onClose,
      }}
      onClose={onClose}>
      <VirtualizedList
        className="flex h-full"
        contentContainerClassName="pb-24"
        data={data}
        endReached={fetchMore}
        itemContent={itemContent}
      />
    </DefaultModal>
  );
}

export default ImpactedItemsDialog;
